// src/components/sections/HomeFeatureCards.jsx
import React, { useRef } from 'react'
import { motion as Motion, useInView } from 'framer-motion'
import { LazyImage } from '../common/LazyImage.jsx'
import './HomeFeatureCards.scss'

export default function HomeFeatureCards({ sectionData }) {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { amount: 0.1 })

    // SKELETON (functionality unchanged – still shows placeholders when no sectionData)
    if (!sectionData) {
        return (
            <section className="engineering-services" ref={sectionRef}>
                <div className="container">
                    <div className="section-header animate-pulse">
                        <div className="h-8 bg-black-200 rounded w-1/2 mx-auto mb-4" />
                        <div className="h-4 bg-black-200 rounded w-2/3 mx-auto" />
                    </div>
                    <div className="services-grid">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="service-card">
                                <div className="card-image">
                                    <div className="w-full h-full bg-black-200 animate-pulse" />
                                </div>
                                <div className="card-content">
                                    <div className="h-4 bg-black-200 rounded w-2/3 mb-3" />
                                    <div className="h-3 bg-black-200 rounded w-full mb-2" />
                                    <div className="h-3 bg-black-200 rounded w-3/4" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        )
    }

    const { heading, subheading, cards = [] } = sectionData

    if (cards.length === 0) {
        return null
    }

    const containerVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                staggerChildren: 0.2,
                when: 'beforeChildren',
            },
        },
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: 'easeOut',
            },
        },
    }

    return (
        <section className="engineering-services" ref={sectionRef}>
            <div className="container">
                {/* Header – mapped v2 -> v1 UI */}
                <Motion.div
                    className="section-header"
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                    variants={containerVariants}
                >
                    {heading && (
                        <Motion.h2 variants={itemVariants}>
                            {heading.toUpperCase()}
                        </Motion.h2>
                    )}
                    {subheading && (
                        <Motion.p
                            className="subheading"
                            variants={itemVariants}
                        >
                            {subheading}
                        </Motion.p>
                    )}
                </Motion.div>

                {/* Cards – v2 data, v1 layout, LazyImage kept */}
                <Motion.div
                    className="services-grid"
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                    variants={containerVariants}
                >
                    {cards.map((card, index) => (
                        <Motion.div
                            key={index}
                            className="service-card"
                            variants={itemVariants}
                            whileHover={{ y: -10, transition: { duration: 0.2 } }}
                        >
                            <div className="card-image">
                                <LazyImage
                                    src={card.imageUrl}
                                    alt={card.title}
                                    className=""
                                    placeholder={
                                        <div className="w-full h-full bg-black-200" />
                                    }
                                />
                            </div>
                            <div className="card-content">
                                <h3>{card.title}</h3>
                                <p>{card.subtitle}</p>
                            </div>
                        </Motion.div>
                    ))}
                </Motion.div>
            </div>
        </section>
    )
}
